import { useLocalStorage } from 'react-use';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { StringParam, useQueryParam } from 'use-query-params';
import {
  CheckoutType,
  FeatureBit,
  isFeatureBitEnabled,
  StoreAnalysis,
  StoreDetails,
  useAuthApi,
} from '@/webapi/use-auth-api';
import { isBrowser, nav, setHttpsPrefix } from '@/utils/browser';
import { Pages } from '@/webapi/pages';
import { UserStatsKind, useUserStats } from '@/webapi/use-user-stats';
import { useShopifyApi } from '@/webapi/use-shopify-api';
import { StoreStatsKind, useStoreStats } from '@/webapi/use-store-stats';
import { Subscription } from '@/features/shop-settings/useShopifySubscriptionDetails';
import { useMergedSubscription } from '@/features/shop-settings/useMergedSubscription';
import { maybe } from '@/features/details/utils';
import { Role } from '@/utils/definitions';
import {
  GeneralStoreSettingsInner,
  SessionType,
  useStoreSettingsApi,
} from '@/webapi/use-store-settings-api';
import { useDetachedState } from '@/components/hooks/use-detached-state';
import { useBillingApi } from '@/webapi/use-billing-api';
import { useUnsavedExperience } from '@/components/hooks/use-unsaved-experience';
import { Experience } from '@/webapi/use-experience-api';

export const ACCOUNT_DETAILS_KEY = `loomi_account`;
const ACCOUNT_DETAILS_WINDOW_KEY = `vsly_loggedin_account`;

export const AccountContext = React.createContext({} as AccountContextProps);

export function createAccountContext(): AccountContextProps {
  const [_account, _setAccount] = useLocalStorage(
    ACCOUNT_DETAILS_KEY,
    emptyAccountDetails(),
  );
  const accountRef = useRef(_account);
  const account = isBrowser()
    ? window?.[ACCOUNT_DETAILS_WINDOW_KEY] || _account
    : _account;

  const [defaultSessionView, setDefaultSessionView] =
    useDetachedState<SessionType>(SessionType.SESSIONS);

  const [generalStoreSettings, setGeneralStoreSettings] = useState<
    GeneralStoreSettingsInner | undefined
  >(undefined);

  const { getDefaultSessionView, getGeneralStoreSettings } =
    useStoreSettingsApi();

  const { getBillingSummary } = useBillingApi();

  const [estimatedOrders, setEstimatedOrders] = useState<number>(0);

  const { isAppEmbedEnabled, loading: isAppEmbedStatusLoading } = useShopifyApi(
    true,
    account?.store?.alias,
  );

  const [appEmbedStatus, setAppEmbedStatus] = useState<AppEmbedStatus>({
    isInit: false,
    isEnabled: false,
    deepLink: ``,
  });

  const [isAppEmbedModalVisible, setIsAppEmbedModalVisible] =
    useState<boolean>(false);

  function setAccount(account: any) {
    _setAccount(account);
    window[ACCOUNT_DETAILS_WINDOW_KEY] = account;
    accountRef.current = account;
  }

  const { getUserContext, logout: deleteCookie, loading } = useAuthApi();

  const { trackIfGoalNotReached, isGoalReached, isDuplicate } =
    useUserStats(account);

  const {
    trackIfGoalNotReached: trackIfStoreGoalNotReached,
    isGoalReached: isStoreGoalReached,
    isDuplicate: isStoreGoalIsDuplicate,
  } = useStoreStats(account);

  const { subscription } = useMergedSubscription(account);

  const { unsavedExperience, isUnsavedExperienceLoading } =
    useUnsavedExperience(account?.store?.alias);

  const isTrialInactive = useMemo(() => {
    const currentDate = new Date();
    const trialEnds = maybe(() => new Date(subscription.trialEnds));
    return currentDate >= trialEnds;
  }, [subscription]);

  const trialInfo = useMemo(() => {
    const trialEnds = subscription?.trialEnds;
    if (!trialEnds) return null;

    const trialEndsDate = new Date(trialEnds);
    const currentDate = new Date();

    if (trialEndsDate <= currentDate) return null;

    const timeDiff = trialEndsDate.getTime() - currentDate.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return { daysRemaining };
  }, [subscription?.trialEnds]);

  const fetchUserContext = async () => {
    const resp = await getUserContext();
    if (resp?.domain) {
      resp.domain = setHttpsPrefix(resp.domain);
      if (
        resp?.featureBits?.[`enable-test-store-publish`] &&
        !!resp?.isTestStore
      ) {
        resp.isTestStore = false;
      }
      setAccount({ isLoggedIn: true, store: resp });
      window[`vsly_fbs`] = resp.featureBits;
      setParallelFlagsFlag();

      try {
        const billingSummary = await getBillingSummary(resp.alias);
        setEstimatedOrders(
          billingSummary?.estimatedOrders || resp.fastSyncOrdersCount || 0,
        );
      } catch {
        setEstimatedOrders(resp.fastSyncOrdersCount || 0);
      }
    }

    const sessionResp = await getDefaultSessionView();
    if (sessionResp) {
      setDefaultSessionView(sessionResp);
    }

    const settings = await getGeneralStoreSettings();
    if (settings) {
      setGeneralStoreSettings(settings.inner);
    }

    return resp;
  };

  function setParallelFlagsFlag() {
    window.parallelTabs = true;
    window.nativeLocalStorage.setItem(`parallelTabs`, `${true}`);
  }

  const logout = async () => {
    await deleteCookie();
    localStorage.clear();
    setAccount(emptyAccountDetails());
    window.location.replace(`https://${window.location.hostname}/`);
  };

  const isLoggedIn = () =>
    accountRef.current.isLoggedIn &&
    !!localStorage.getItem(ACCOUNT_DETAILS_KEY);

  const invalidSubscription = () => isInvalidSubscription(accountRef.current);

  useEffect(() => {
    if (accountRef.current?.store?.alias) {
      console.debug(
        `vsly`,
        `checking for app embed status`,
        accountRef.current?.store?.alias,
      );
      refreshAppEmbedStatus(accountRef.current?.store?.alias).then();
    } else {
      setAppEmbedStatus({ isInit: false, isEnabled: false, deepLink: `` });
    }
  }, [accountRef?.current?.store?.alias]);

  useEffect(() => {
    console.debug(
      `vsly`,
      `app embed status changed`,
      account?.store?.alias,
      appEmbedStatus,
    );
  }, [appEmbedStatus]);

  const refreshAppEmbedStatus = async (alias: string) => {
    const resp = await isAppEmbedEnabled();
    if (resp && accountRef?.current?.store?.alias === alias) {
      setAppEmbedStatus({
        isInit: true,
        isEnabled: resp.isAppBlockEnabled,
        deepLink: resp.deepLink,
        legacy_sdk_installed: !!resp?.legacy_sdk_installed,
      });
    } else {
      setAppEmbedStatus({ isInit: false, isEnabled: false, deepLink: `` });
    }
    return Promise.resolve();
  };

  const showAppEmbedModalIfNeeded = () => {
    if (isAppEmbedRequired()) {
      setIsAppEmbedModalVisible(true);
      return true;
    }
    return false;
  };

  const isAppEmbedRequired = useCallback(
    () =>
      appEmbedStatus.isInit &&
      appEmbedStatus.isEnabled === false &&
      !account?.store?.isTestStore,
    [appEmbedStatus, account],
  );

  const shouldShowBookDemo = useMemo(() => {
    if (account?.store?.isTestStore === true) {
      return false;
    }
    return estimatedOrders > 3000;
  }, [estimatedOrders, account?.store?.isTestStore]);

  const hasHighMonthlyOrders = useMemo(
    () => shouldShowBookDemo && estimatedOrders > 7500,
    [shouldShowBookDemo, estimatedOrders],
  );

  const setSdkInjectStatus = (b: boolean) => {
    setAppEmbedStatus((prevState) => ({
      ...prevState,
      isEnabled: b,
      legacy_sdk_installed: b,
    }));
  };

  const checkoutType = useMemo(() => {
    if (
      account?.store?.storeAnalysis?.checkoutType ===
      CheckoutType.SHOPIFY_LEGACY_CHECKOUT
    ) {
      return CheckoutType.SHOPIFY_LEGACY_CHECKOUT;
    }
    return CheckoutType.SHOPIFY_CHECKOUT_EXTENSIBILITY;
  }, [account]);

  const storeAnalysis = useMemo(() => account?.store?.storeAnalysis, [account]);

  const isCartSelectorExist = useMemo(
    () =>
      typeof storeAnalysis?.cartRootSelector === `string` &&
      storeAnalysis?.cartRootSelector.trim() !== ``,
    [storeAnalysis],
  );

  return {
    isLoggedIn,
    invalidSubscription,
    account,
    fetchUserContext,
    logout,
    loading,
    trackIfGoalNotReached,
    isGoalReached,
    isDuplicateGoal: isDuplicate,
    trackIfStoreGoalNotReached,
    isStoreGoalReached,
    isStoreGoalIsDuplicate,
    appEmbedStatus,
    isAppEmbedStatusLoading,
    refreshAppEmbedStatus,
    setIsAppEmbedModalVisible,
    isAppEmbedModalVisible,
    showAppEmbedModalIfNeeded,
    isAppEmbedRequired,
    shouldShowBookDemo,
    hasHighMonthlyOrders,
    setSdkInjectStatus,
    isTrialInactive,
    trialInfo,
    subscription,
    checkoutType,
    storeAnalysis,
    isCartSelectorExist,
    defaultSessionView,
    setGeneralStoreSettings,
    generalStoreSettings,
    unsavedExperience,
    isUnsavedExperienceLoading,
    estimatedOrders,
  };
}

export function useFeatureBit(fb: FeatureBit, fallback = false): boolean {
  const { account } = useContext(AccountContext);
  return isFeatureBitEnabled(account?.store?.featureBits, fb, fallback);
}

export function isCopilotEnabled(): boolean {
  const shouldKillOverride = useFeatureBit(
    FeatureBit.KILL_PROMPT_TO_EXPERIENCE_OVERRIDE,
    false,
  );

  if (!shouldKillOverride) {
    const { account, estimatedOrders } = useContext(AccountContext);
    let isVSB = false;
    if (typeof estimatedOrders !== `undefined`) {
      isVSB = !account?.store?.isTestStore && estimatedOrders <= 1000;
    } else {
      isVSB =
        !account?.store?.isTestStore &&
        account?.store?.fastSyncOrdersCount > 0 &&
        account?.store?.fastSyncOrdersCount < 1000;
    }

    if (isVSB) {
      return true;
    }
  }

  return useFeatureBit(FeatureBit.PROMPT_TO_EXPERIENCE, false);
}

export function useFetchUserContext(
  accountCtx: AccountContextProps,
  pathName?: string | undefined,
) {
  const [, setAliasQp] = useQueryParam(`al`, StringParam);

  useEffect(() => {
    (async () => {
      try {
        if (isPrivatePath(pathName)) {
          const details = await accountCtx.fetchUserContext();
          if (window?.parallelTabs && !!details?.alias) {
            setAliasQp(details.alias);
          }
          if (
            !isAccountSelect(pathName) &&
            accountCtx.invalidSubscription() &&
            accountCtx.account.store.role !== Role.SYSTEM
          ) {
            await nav(Pages.PLEASE_REINSTALL);
          }
        }
      } catch (e) {
        console.warn(`useFetchUserContext: unexpected failure`, e);
      }
    })();
  }, [pathName]);
}

function isAccountSelect(pathname: string) {
  return pathname?.startsWith(`/accounts`);
}

export function isPrivatePath(pathname: string): boolean {
  if (pathname === `/`) return false;

  return [
    `/public`,
    `/welcome`,
    `/signup`,
    `/await-email`,
    `/login`,
    `/404`,
    `/pricing`,
    `/product-demo`,
  ]
    ?.map((x) => !pathname?.startsWith(x))
    ?.reduce((prev, curr) => prev && curr);
}

export interface AccountContextProps {
  isLoggedIn: () => boolean;
  invalidSubscription: () => boolean;
  account: AccountDetails;
  fetchUserContext: () => Promise<StoreDetails>;
  logout: () => Promise<void>;
  loading: boolean;
  trackIfGoalNotReached: (
    kind: UserStatsKind,
    ...dedupKey: string[]
  ) => Promise<boolean>;
  isGoalReached: (kind: UserStatsKind) => boolean;
  isDuplicateGoal: (dedupKey: string) => boolean;
  trackIfStoreGoalNotReached: (
    kind: StoreStatsKind,
    ...dedupKey: string[]
  ) => Promise<boolean>;
  isStoreGoalReached: (kind: StoreStatsKind) => boolean;
  isStoreGoalIsDuplicate: (dedupKey: string) => boolean;
  appEmbedStatus?: AppEmbedStatus;
  isAppEmbedStatusLoading: boolean;
  refreshAppEmbedStatus: (alias: string) => Promise<void>;
  setIsAppEmbedModalVisible: (isVisible: boolean) => void;
  isAppEmbedModalVisible: boolean;
  showAppEmbedModalIfNeeded: () => boolean;
  isAppEmbedRequired: () => boolean;
  shouldShowBookDemo: boolean;
  hasHighMonthlyOrders: boolean;
  setSdkInjectStatus: (b: boolean) => void;
  isTrialInactive: boolean;
  trialInfo: { daysRemaining: number } | null;
  subscription: Subscription;
  checkoutType: CheckoutType;
  storeAnalysis: StoreAnalysis;
  isCartSelectorExist: boolean;
  defaultSessionView?: SessionType;
  generalStoreSettings?: GeneralStoreSettingsInner;
  setGeneralStoreSettings?: (
    settings: GeneralStoreSettingsInner | undefined,
  ) => void;
  unsavedExperience: Experience | null;
  isUnsavedExperienceLoading: boolean;
  estimatedOrders?: number;
}

export interface AccountDetails {
  isLoggedIn: boolean;
  store: StoreDetails;
}

export interface AppEmbedStatus {
  isInit: boolean;
  isEnabled: boolean;
  legacy_sdk_installed?: boolean;
  deepLink: string;
  alias?: string;
}

const emptyAccountDetails = (): AccountDetails => ({
  isLoggedIn: false,
  store: undefined,
});

export function isInvalidSubscription(account: AccountDetails) {
  const subscription = account?.store?.subscription;
  const isUninstalled = account?.store?.isUninstalled;
  const notActive = !!subscription && subscription?.status !== `ACTIVE`;
  return isUninstalled || notActive;
}

export function useLoggedInAccount(): AccountDetails {
  const windowValue = window?.[ACCOUNT_DETAILS_WINDOW_KEY];
  if (windowValue) {
    return windowValue;
  }
  const value = localStorage.getItem(ACCOUNT_DETAILS_KEY);
  if (value) {
    try {
      const lsVal = JSON.parse(value);
      window[ACCOUNT_DETAILS_WINDOW_KEY] = lsVal;
      return lsVal;
    } catch (e) {
      console.warn(`Failed to parse account details from localStorage`, e);
    }
  }
  return emptyAccountDetails();
}
